import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  ListGroup,
  Badge,
  Spinner,
  Collapse,
} from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { useI18n } from "../i18n";
import {
  formatDateShort,
  formatTime,
  formatCurrency,
} from "../utils/formatters";
import ItalianDatePicker from "./ItalianDatePicker";
import AirportSelector from "./AirportSelector";

interface Airport {
  code: string;
  name: string;
  country: string;
}

interface FlightOption {
  flight_number: string;
  flight_key?: string | null;
  price: number;
  original_price?: number;
  original_currency?: string;
  date: string;
  departure_time: string;
  arrival_time: string;
  duration: string;
  day_of_week: string;
}

interface CheapestDaysResults {
  outbound: FlightOption[];
  return: FlightOption[];
}

interface CheapestDaysModalProps {
  show: boolean;
  onHide: () => void;
  onTrackerAdded?: (hasFlightData?: boolean) => void;
}

const CheapestDaysModal: React.FC<CheapestDaysModalProps> = ({
  show,
  onHide,
  onTrackerAdded,
}) => {
  const [airports, setAirports] = useState<Airport[]>([]);
  const [availableDestinations, setAvailableDestinations] = useState<Airport[]>([]);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState<string | null>(null);
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [mode, setMode] = useState<"month" | "range">("month");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [roundtrip, setRoundtrip] = useState(true);
  const [maxPrice, setMaxPrice] = useState<string>("");
  const [results, setResults] = useState<CheapestDaysResults>({ outbound: [], return: [] });
  const [loading, setLoading] = useState(false);
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [destinationsError, setDestinationsError] = useState("");
  const [error, setError] = useState("");
  const [selectedOutbound, setSelectedOutbound] = useState<FlightOption | null>(null);
  const [selectedReturn, setSelectedReturn] = useState<FlightOption | null>(null);
  const [creatingTracker, setCreatingTracker] = useState(false);
  const [searchFormCollapsed, setSearchFormCollapsed] = useState(false);
  const { t, get, lang } = useI18n();
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadAirports = useCallback(async () => {
    try {
      const response = await axios.get("/api/airports");
      setAirports(response.data);
    } catch (error) {
      console.error("Errore nel caricamento degli aeroporti:", error);
      toast.error(t("messages.airportsLoadError"));
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadAvailableDestinations = useCallback(async (originCode: string) => {
    if (!originCode) {
      setAvailableDestinations(airports);
      setDestinationsError("");
      return;
    }

    setLoadingDestinations(true);
    setDestinationsError("");
    try {
      const response = await axios.get(`/api/airports/destinations/${originCode}`);
      setAvailableDestinations(response.data);
      setDestinationsError("");
    } catch (error) {
      console.error("Error loading available destinations:", error);
      setAvailableDestinations([]);
      setDestinationsError(t("addTracker.destinationsLoadError") || "Unable to retrieve available airports");
    } finally {
      setLoadingDestinations(false);
    }
  }, [airports]);

  useEffect(() => {
    if (show) {
      loadAirports();
      const now = new Date();
      setOrigin("");
      setDestination(null);
      setMonth((now.getMonth() + 1).toString());
      setYear(now.getFullYear().toString());
      setMode("month");
      setStartDate(null);
      setEndDate(null);
      setRoundtrip(true);
      setMaxPrice("");
      setResults({ outbound: [], return: [] });
      setError("");
    }
  }, [show, loadAirports]);

  // Load available destinations when origin changes
  useEffect(() => {
    if (origin && airports.length > 0) {
      // Clear destination and results immediately when origin changes
      setDestination(null);
      setResults({ outbound: [], return: [] });
      loadAvailableDestinations(origin);
    } else {
      setAvailableDestinations(airports);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, airports, loadAvailableDestinations]);

  // Clear results when destination changes
  useEffect(() => {
    setResults({ outbound: [], return: [] });
  }, [destination]);

  const formatDateToISO = (date: Date | null): string | null => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setResults({ outbound: [], return: [] });

    try {
      if (origin === destination) {
        setError(t("addTracker.sameAirportError"));
        setLoading(false);
        return;
      }

      const payload: any = { origin, destination, language: lang, roundtrip };

      // Add max_price if specified
      if (maxPrice && parseFloat(maxPrice) > 0) {
        payload.max_price = parseFloat(maxPrice);
      }

      if (mode === "month") {
        payload.month = parseInt(month);
        payload.year = parseInt(year);
      } else {
        const startISO = formatDateToISO(startDate);
        const endISO = formatDateToISO(endDate || startDate);
        if (!startISO) {
          setError(t("cheapestDays.startDateRequired"));
          setLoading(false);
          return;
        }
        // Validate max 28 days
        const startObj = new Date(startISO);
        const endObj = endISO ? new Date(endISO) : startObj;
        const diffDays = Math.floor(
          (endObj.getTime() - startObj.getTime()) / (1000 * 60 * 60 * 24),
        );
        if (diffDays > 27) {
          setError(t("cheapestDays.intervalTooLong"));
          setLoading(false);
          return;
        }
        payload.start_date = startISO;
        payload.end_date = endISO;
      }

      const response = await axios.post("/api/cheapest-days", payload);

      if (response.data && (response.data.outbound?.length > 0 || response.data.return?.length > 0)) {
        setResults(response.data);
      } else {
        setError(t("cheapestDays.noFlightsFound"));
      }
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        t("cheapestDays.searchError");
      setError(errorMsg);
      toast.error(errorMsg);
      console.error("Errore nella ricerca:", err);
    } finally {
      setLoading(false);
    }
  };

  // Reset selections when search results change
  useEffect(() => {
    setSelectedOutbound(null);
    setSelectedReturn(null);
    // Auto-collapse search form on mobile when results are loaded
    if ((results.outbound.length > 0 || results.return.length > 0) && window.innerWidth < 768) {
      setSearchFormCollapsed(true);
    }
  }, [results]);

  // Helper function to parse date/time correctly regardless of format
  const parseDateTimeString = (dateStr: string, timeStr: string): Date => {
    // If timeStr already contains full datetime (YYYY-MM-DDTHH:MM:SS), use it directly
    if (timeStr && timeStr.includes('T')) {
      return new Date(timeStr);
    }
    // If timeStr already contains the date prefix, extract just the time part
    if (timeStr && timeStr.length > 10 && timeStr.includes('-')) {
      const timePart = timeStr.substring(timeStr.lastIndexOf('T') + 1);
      return new Date(`${dateStr}T${timePart}`);
    }
    // Standard case: combine date and time
    return new Date(`${dateStr}T${timeStr}`);
  };

  // Helper function to check if a flight is in the past
  const isFlightInPast = (flight: FlightOption): boolean => {
    if (!flight.date || !flight.departure_time) return false;
    const now = new Date();
    const flightDateTime = parseDateTimeString(flight.date, flight.departure_time);
    return !isNaN(flightDateTime.getTime()) && flightDateTime < now;
  };

  // Helper function to check if a flight is compatible with the selected flight from the other direction
  const isFlightCompatible = (flight: FlightOption, isOutbound: boolean): boolean => {
    // Check if flight is in the past
    if (isFlightInPast(flight)) {
      return false;
    }

    if (!roundtrip) return true; // No restrictions for one-way
    
    if (isOutbound) {
      // Checking outbound flight compatibility when return is selected
      if (!selectedReturn) return true; // No return selected, all outbound flights are valid
      
      // Validate that we have the necessary data
      if (!flight.date || !flight.arrival_time || !selectedReturn.date || !selectedReturn.departure_time) {
        return true; // If data is missing, don't disable
      }
      
      // Outbound arrival must be before or at same time as return departure
      const outboundArrival = parseDateTimeString(flight.date, flight.arrival_time);
      const returnDeparture = parseDateTimeString(selectedReturn.date, selectedReturn.departure_time);
      
      // Validate dates are valid
      if (isNaN(outboundArrival.getTime()) || isNaN(returnDeparture.getTime())) {
        return true; // If dates are invalid, don't disable
      }
      
      return outboundArrival <= returnDeparture;
    } else {
      // Checking return flight compatibility when outbound is selected
      if (!selectedOutbound) return true; // No outbound selected, all return flights are valid
      
      // Validate that we have the necessary data
      if (!flight.date || !flight.departure_time || !selectedOutbound.date || !selectedOutbound.arrival_time) {
        return true; // If data is missing, don't disable
      }
      
      // Return departure must be after or at same time as outbound arrival
      const outboundArrival = parseDateTimeString(selectedOutbound.date, selectedOutbound.arrival_time);
      const returnDeparture = parseDateTimeString(flight.date, flight.departure_time);
      
      // Validate dates are valid
      if (isNaN(outboundArrival.getTime()) || isNaN(returnDeparture.getTime())) {
        return true; // If dates are invalid, don't disable
      }
      
      return returnDeparture >= outboundArrival;
    }
  };

  // Handle creating tracker from selected flights
  const handleCreateTracker = async () => {
    // Validation
    if (!selectedOutbound) {
      toast.error(t("cheapestDays.selectOutboundFlight"));
      return;
    }

    if (roundtrip && !selectedReturn) {
      toast.error(t("cheapestDays.selectBothFlights"));
      return;
    }

    setCreatingTracker(true);
    try {
      // Send flight details to avoid re-fetching from API
      await axios.post("/api/trackers", {
        origin,
        destination,
        departure_date: selectedOutbound.date,
        return_date: roundtrip && selectedReturn ? selectedReturn.date : null,
        threshold_price: null, // User can set threshold later
        outbound_flight: {
          flight_number: selectedOutbound.flight_number,
          flight_key: selectedOutbound.flight_key,
          price: selectedOutbound.price,
          original_price: selectedOutbound.original_price,
          original_currency: selectedOutbound.original_currency,
          date: selectedOutbound.date,
          departure_time: selectedOutbound.departure_time,
          arrival_time: selectedOutbound.arrival_time,
          duration: selectedOutbound.duration,
          day_of_week: selectedOutbound.day_of_week,
        },
        return_flight: roundtrip && selectedReturn ? {
          flight_number: selectedReturn.flight_number,
          flight_key: selectedReturn.flight_key,
          price: selectedReturn.price,
          original_price: selectedReturn.original_price,
          original_currency: selectedReturn.original_currency,
          date: selectedReturn.date,
          departure_time: selectedReturn.departure_time,
          arrival_time: selectedReturn.arrival_time,
          duration: selectedReturn.duration,
          day_of_week: selectedReturn.day_of_week,
        } : null,
      });

      toast.success(t("cheapestDays.trackerCreatedSuccess"));
      
      // Reset selections
      setSelectedOutbound(null);
      setSelectedReturn(null);
      
      // Call callback - pass true to indicate flight data is already stored
      if (onTrackerAdded) {
        onTrackerAdded(true);
      }
      
      // Close modal
      onHide();
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        t("cheapestDays.trackerCreationError");
      toast.error(errorMsg);
      console.error("Error creating tracker:", err);
    } finally {
      setCreatingTracker(false);
    }
  };

  // Group outbound flights by date
  const outboundFlightsByDate = results.outbound.reduce(
    (acc, flight) => {
      if (!acc[flight.date]) {
        acc[flight.date] = [];
      }
      acc[flight.date].push(flight);
      return acc;
    },
    {} as Record<string, FlightOption[]>,
  );

  // Group return flights by date
  const returnFlightsByDate = results.return.reduce(
    (acc, flight) => {
      if (!acc[flight.date]) {
        acc[flight.date] = [];
      }
      acc[flight.date].push(flight);
      return acc;
    },
    {} as Record<string, FlightOption[]>,
  );

  // Get all unique dates from both directions and sort
  const allDates = new Set([...Object.keys(outboundFlightsByDate), ...Object.keys(returnFlightsByDate)]);
  const sortedDates = Array.from(allDates).sort();

  const isSameAirport = !!(origin && destination && origin === destination);

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-calendar-check"></i> {t("cheapestDays.title")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Collapse toggle button - only show when results exist */}
        {(results.outbound.length > 0 || results.return.length > 0) && (
          <div className="mb-3">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setSearchFormCollapsed(!searchFormCollapsed)}
              className="w-100"
            >
              <i className={`bi bi-chevron-${searchFormCollapsed ? 'down' : 'up'} me-2`}></i>
              {searchFormCollapsed ? t("cheapestDays.showSearchForm") || "Show Search Form" : t("cheapestDays.hideSearchForm") || "Hide Search Form"}
            </Button>
          </div>
        )}
        
        <Collapse in={!searchFormCollapsed}>
          <div>
        <Form onSubmit={handleSubmit}>
          <div className="row mb-3">
            <div className="col-md-12">
              <Form.Label>{t("cheapestDays.modeLabel")}</Form.Label>
              <div className="d-flex flex-column flex-md-row gap-3">
                <Form.Check
                  type="radio"
                  label={t("cheapestDays.modeMonthLabel")}
                  name="mode"
                  checked={mode === "month"}
                  onChange={() => setMode("month")}
                />
                <Form.Check
                  type="radio"
                  label={t("cheapestDays.modeRangeLabel")}
                  name="mode"
                  checked={mode === "range"}
                  onChange={() => setMode("range")}
                />
              </div>
              <Form.Text className="text-muted">{t("cheapestDays.note")}</Form.Text>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="fw-semibold d-flex align-items-center">
                  <i className="bi bi-geo-alt-fill text-primary me-2"></i>
                  {t("cheapestDays.originLabel")}
                </Form.Label>
                <AirportSelector
                  airports={airports}
                  value={origin}
                  onChange={(value) => setOrigin(value || "")}
                  placeholder={t("addTracker.selectOrigin") || "Select origin airport..."}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="fw-semibold d-flex align-items-center">
                  <i className="bi bi-geo-fill text-success me-2"></i>
                  {t("cheapestDays.destinationLabel")}
                </Form.Label>
                <AirportSelector
                  airports={loadingDestinations ? [] : availableDestinations}
                  value={destination}
                  onChange={(value) => setDestination(value)}
                  placeholder={
                    loadingDestinations 
                      ? "Loading available destinations..." 
                      : destinationsError
                      ? destinationsError
                      : t("addTracker.selectDestination") || "Select destination airport..."
                  }
                  isDisabled={loadingDestinations || !!destinationsError}
                />
                {loadingDestinations && (
                  <Form.Text className="text-muted d-flex align-items-center mt-2">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading available destinations from {origin}...
                  </Form.Text>
                )}
                {destinationsError && !loadingDestinations && (
                  <Form.Text className="text-danger d-flex align-items-center mt-2">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {destinationsError}
                  </Form.Text>
                )}
              </Form.Group>
            </div>
            {isSameAirport && (
              <div className="col-12 mt-2">
                <Form.Text className="text-danger d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {t("addTracker.sameAirportError")}
                </Form.Text>
              </div>
            )}
          </div>

          {/* Search Options Row */}
          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label={
                    <span className="fw-semibold">
                      <i className="bi bi-arrow-left-right text-info me-2"></i>
                      {t("cheapestDays.roundtripLabel")}
                    </span>
                  }
                  checked={roundtrip}
                  onChange={(e) => setRoundtrip(e.target.checked)}
                />
                <Form.Text className="text-muted d-block mt-1">
                  {t("cheapestDays.roundtripHelpText")}
                </Form.Text>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  <i className="bi bi-currency-euro text-warning me-2"></i>
                  {t("cheapestDays.maxPriceLabel")}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={t("cheapestDays.maxPricePlaceholder")}
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                />
                <Form.Text className="text-muted">
                  {t("cheapestDays.maxPriceHelpText")}
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          {mode === "month" ? (
            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>{t("cheapestDays.monthLabel")}</Form.Label>
                  <Form.Select
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    required
                  >
                      {((get("months") as string[]) || []).map((m: string, idx: number) => (
                        <option key={idx + 1} value={idx + 1}>
                          {m}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Anno</Form.Label>
                  <Form.Select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    required
                  >
                    <option value={currentYear}>{currentYear}</option>
                    <option value={nextYear}>{nextYear}</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          ) : (
            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>{t("cheapestDays.startDateLabel")}</Form.Label>
                  <ItalianDatePicker
                    selected={startDate}
                    onChange={(date) => {
                      setStartDate(date);
                      // Only adjust end date if current range would exceed 4 weeks
                      if (date && endDate) {
                        const daysDiff = Math.floor((endDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
                        if (daysDiff > 27) {
                          const newEndDate = new Date(date.getTime() + 27 * 24 * 60 * 60 * 1000);
                          setEndDate(newEndDate);
                        }
                      } else if (date && !endDate) {
                        // If no end date set, set it to 4 weeks from start
                        const newEndDate = new Date(date.getTime() + 27 * 24 * 60 * 60 * 1000);
                        setEndDate(newEndDate);
                      }
                    }}
                    minDate={new Date()}
                    placeholderText={t("cheapestDays.startDatePlaceholder")}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>{t("cheapestDays.endDateLabel")}</Form.Label>
                  <ItalianDatePicker
                    selected={endDate}
                    onChange={(date) => {
                      setEndDate(date);
                      // Only adjust start date if current range would exceed 4 weeks
                      if (date && startDate) {
                        const daysDiff = Math.floor((date.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                        if (daysDiff > 27) {
                          const newStartDate = new Date(date.getTime() - 27 * 24 * 60 * 60 * 1000);
                          setStartDate(newStartDate);
                        }
                      } else if (date && !startDate) {
                        // If no start date set, set it to 4 weeks before end
                        const newStartDate = new Date(date.getTime() - 27 * 24 * 60 * 60 * 1000);
                        setStartDate(newStartDate);
                      }
                    }}
                    minDate={new Date()}
                    placeholderText={t("cheapestDays.endDatePlaceholder")}
                  />
                  <Form.Text className="text-muted">
                      {t("cheapestDays.endDateHelpText")}
                  </Form.Text>
                </Form.Group>
              </div>
            </div>
          )}

          <Button
            variant="primary"
            type="submit"
            className="w-100"
            disabled={loading || !origin || !destination}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t("cheapestDays.searchingText")}
              </>
            ) : (
              <>
                <i className="bi bi-search"></i> {t("cheapestDays.findCheapestDaysButton")}
              </>
            )}
          </Button>
        </Form>
          </div>
        </Collapse>

        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}

        {(results.outbound.length > 0 || results.return.length > 0) && (
          <>
            <hr className="mt-4" />
            <h6 className="mb-3">
              <i className="bi bi-calendar2-week"></i> {t("cheapestDays.cheapestFlightsTitle")}
            </h6>
            
            {/* Two column layout for roundtrip, single column for one-way */}
            <div className="row">
              {/* Left column - Outbound flights */}
              <div className={roundtrip ? "col-md-6" : "col-md-12"}>
                <div className="mb-3">
                  <h6 className="text-primary">
                    <i className="bi bi-airplane-fill"></i> {origin} → {destination}
                  </h6>
                  <small className="text-muted">{t("cheapestDays.outboundFlights")}</small>
                </div>
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {results.outbound.length > 0 ? (
                    sortedDates.map((date) => {
                      const flights = outboundFlightsByDate[date];
                      if (!flights) return null;
                      const firstFlight = flights[0];

                      return (
                        <div key={date} className="mb-3">
                          <div className="bg-light p-2 rounded mb-2">
                            <strong>{formatDateShort(date)}</strong>
                            <span className="text-muted ms-2">
                              ({firstFlight.day_of_week})
                            </span>
                          </div>
                          <ListGroup>
                            {flights.map((flight, idx) => {
                              const isSelected = selectedOutbound?.date === flight.date && 
                                               selectedOutbound?.flight_number === flight.flight_number &&
                                               selectedOutbound?.departure_time === flight.departure_time;
                              const isCompatible = isFlightCompatible(flight, true);
                              const isDisabled = !isCompatible;
                              
                              return (
                                <ListGroup.Item 
                                  key={idx}
                                  active={isSelected}
                                  disabled={isDisabled}
                                  style={{ 
                                    cursor: isDisabled ? "not-allowed" : "pointer",
                                    opacity: isDisabled ? 0.5 : 1,
                                  }}
                                  onClick={() => {
                                    if (!isDisabled) {
                                      // Toggle selection for checkboxes, direct select for radio
                                      if (roundtrip && isSelected) {
                                        setSelectedOutbound(null);
                                      } else {
                                        setSelectedOutbound(flight);
                                      }
                                    }
                                  }}
                                  className={isSelected ? "selected-flight" : ""}
                                >
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                      <div className="d-flex align-items-center mb-1">
                                        <Form.Check
                                          type={roundtrip ? "checkbox" : "radio"}
                                          name="outbound-flight"
                                          checked={isSelected}
                                          disabled={isDisabled}
                                          onChange={() => {
                                            if (!isDisabled) {
                                              if (roundtrip && isSelected) {
                                                setSelectedOutbound(null);
                                              } else {
                                                setSelectedOutbound(flight);
                                              }
                                            }
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="me-2"
                                        />
                                        <Badge bg="secondary" className="me-2">
                                          {flight.flight_number}
                                        </Badge>
                                        <span className="text-muted small">
                                          {formatTime(flight.departure_time)} -{" "}
                                          {formatTime(flight.arrival_time)}
                                        </span>
                                        <span className="text-muted small ms-2">
                                          ({flight.duration})
                                        </span>
                                      </div>
                                    </div>
                                    <div className="text-end">
                                      <div className={`h5 mb-0 ${isSelected ? 'text-white' : 'text-primary'}`}>
                                        {formatCurrency(flight.price, flight.original_price, flight.original_currency)}
                                      </div>
                                    </div>
                                  </div>
                                </ListGroup.Item>
                              );
                            })}
                          </ListGroup>
                        </div>
                      );
                    })
                  ) : (
                    <Alert variant="info">
                      <i className="bi bi-info-circle"></i> {t("cheapestDays.noOutboundFlights")}
                    </Alert>
                  )}
                </div>
              </div>

              {/* Right column - Return flights (only show if roundtrip is enabled) */}
              {roundtrip && (
                <div className="col-md-6">
                  <div className="mb-3">
                    <h6 className="text-success">
                      <i className="bi bi-airplane-fill" style={{transform: "scaleX(-1)"}}></i> {destination} → {origin}
                    </h6>
                    <small className="text-muted">{t("cheapestDays.returnFlights")}</small>
                  </div>
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    {results.return.length > 0 ? (
                      sortedDates.map((date) => {
                        const flights = returnFlightsByDate[date];
                        if (!flights) return null;
                        const firstFlight = flights[0];

                        return (
                          <div key={date} className="mb-3">
                            <div className="bg-light p-2 rounded mb-2">
                              <strong>{formatDateShort(date)}</strong>
                              <span className="text-muted ms-2">
                                ({firstFlight.day_of_week})
                              </span>
                            </div>
                            <ListGroup>
                              {flights.map((flight, idx) => {
                                const isSelected = selectedReturn?.date === flight.date && 
                                                 selectedReturn?.flight_number === flight.flight_number &&
                                                 selectedReturn?.departure_time === flight.departure_time;
                                const isCompatible = isFlightCompatible(flight, false);
                                const isDisabled = !isCompatible;
                                
                                return (
                                  <ListGroup.Item 
                                    key={idx}
                                    active={isSelected}
                                    disabled={isDisabled}
                                    style={{ 
                                      cursor: isDisabled ? "not-allowed" : "pointer",
                                      opacity: isDisabled ? 0.5 : 1,
                                    }}
                                    onClick={() => {
                                      if (!isDisabled) {
                                        // Toggle selection for checkboxes
                                        if (isSelected) {
                                          setSelectedReturn(null);
                                        } else {
                                          setSelectedReturn(flight);
                                        }
                                      }
                                    }}
                                    className={isSelected ? "selected-flight" : ""}
                                  >
                                    <div className="d-flex justify-content-between align-items-start">
                                      <div className="flex-grow-1">
                                        <div className="d-flex align-items-center mb-1">
                                          <Form.Check
                                            type="checkbox"
                                            name="return-flight"
                                            checked={isSelected}
                                            disabled={isDisabled}
                                            onChange={() => {
                                              if (!isDisabled) {
                                                if (isSelected) {
                                                  setSelectedReturn(null);
                                                } else {
                                                  setSelectedReturn(flight);
                                                }
                                              }
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            className="me-2"
                                          />
                                          <Badge bg="secondary" className="me-2">
                                            {flight.flight_number}
                                          </Badge>
                                          <span className="text-muted small">
                                            {formatTime(flight.departure_time)} -{" "}
                                            {formatTime(flight.arrival_time)}
                                          </span>
                                          <span className="text-muted small ms-2">
                                            ({flight.duration})
                                          </span>
                                        </div>
                                      </div>
                                      <div className="text-end">
                                        <div className={`h5 mb-0 ${isSelected ? 'text-white' : 'text-success'}`}>
                                          {formatCurrency(flight.price, flight.original_price, flight.original_currency)}
                                        </div>
                                      </div>
                                    </div>
                                  </ListGroup.Item>
                                );
                              })}
                            </ListGroup>
                          </div>
                        );
                      })
                    ) : (
                      <Alert variant="info">
                        <i className="bi bi-info-circle"></i> {t("cheapestDays.noReturnFlights")}
                      </Alert>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Create Tracker Button */}
            <div className="mt-4 d-flex justify-content-center">
              <Button
                variant="success"
                size="lg"
                onClick={handleCreateTracker}
                disabled={
                  creatingTracker || 
                  !selectedOutbound || 
                  (roundtrip && !selectedReturn)
                }
              >
                {creatingTracker ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {t("cheapestDays.creatingTracker")}
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle"></i> {t("cheapestDays.createTrackerButton")}
                  </>
                )}
              </Button>
            </div>
          </>
        )}

        {!loading && results.outbound.length === 0 && results.return.length === 0 && !error && (
          <Alert variant="info" className="mt-3">
            <i className="bi bi-info-circle"></i> {t("cheapestDays.infoText")}
          </Alert>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default CheapestDaysModal;
